import { useAppSelector } from '@common/hooks/store';
import { getUserId, selectUserCurrencyCode } from '@features/auth/slice/authSlice';
import { useClaimQuizMutation } from '@features/quiz/api/quizApi';
import useRefreshAfterGrant from '@features/quiz/hooks/useRefreshAfterGrant';
import { claimMessageId, clearPendingClaim, formatQuizSum, isFinalClaimError, readPendingClaim } from '@features/quiz/lib/quiz';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';

const CLAIM_TOAST_MS = 8000;

let claimingToken: string | null = null;

export default function QuizPendingClaim() {
  const navigate = useNavigate();
  const onSignIn = useMatch('/sign-in') !== null;
  const userId = useAppSelector(getUserId);
  const currencyCode = useAppSelector(selectUserCurrencyCode) ?? '';
  const refreshAfterGrant = useRefreshAfterGrant();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [ claimQuiz ] = useClaimQuizMutation();

  useEffect(() => {
    if (onSignIn || !userId) {
      return;
    }
    const pending = readPendingClaim();
    if (!pending || claimingToken === pending.token) {
      return;
    }
    claimingToken = pending.token;
    claimQuiz(pending.token).unwrap()
      .then((result) => {
        clearPendingClaim(pending.token);
        if (result.status !== 'granted') {
          enqueueSnackbar(<FormattedMessage id={claimMessageId(result.status)}/>, {
            variant: 'info',
            autoHideDuration: CLAIM_TOAST_MS,
          });
          return;
        }
        refreshAfterGrant();
        const sum = [ formatQuizSum(result.sum ?? pending.sum), currencyCode ].filter(Boolean).join(' ');
        enqueueSnackbar(<FormattedMessage id="QUIZ_GRANTED" values={{ sum }}/>, {
          variant: 'success',
          autoHideDuration: CLAIM_TOAST_MS,
          action: (key) => (
            <Button
              color="inherit"
              size="small"
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              onClick={() => {
                closeSnackbar(key);
                navigate('/my-freebets');
              }}
            >
              <FormattedMessage id="QUIZ_TO_BONUSES"/>
            </Button>
          ),
        });
      })
      .catch((error) => {
        if (isFinalClaimError(error)) {
          clearPendingClaim(pending.token);
        }
      })
      .finally(() => {
        claimingToken = null;
      });
  }, [ claimQuiz, closeSnackbar, currencyCode, enqueueSnackbar, navigate, onSignIn, refreshAfterGrant, userId ]);

  return null;
}
